<template>
  <div class="fiber-optics-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$θ_入射 > θ_临界 = arcsin(n_2 / n_1)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 500 220">
          
          <!-- Environment (Air n0 = 1.0) -->
          <text x="10" y="20" fill="#64748b" font-size="10">空气介质 n₀=1.00</text>
          
          <!-- Cladding (包层 n2) -->
          <rect x="100" y="40" width="400" height="140" fill="rgba(255,255,255,0.05)" stroke="#334155" stroke-width="2" />
          <text x="400" y="55" fill="#94a3b8" font-size="10">低折射率包层 n₂={{ n2.toFixed(2) }}</text>

          <!-- Core (纤芯 n1) -->
          <rect x="100" y="70" width="400" height="80" fill="rgba(6, 182, 212, 0.2)" stroke="#06b6d4" stroke-dasharray="8,4" />
          <text x="400" y="115" fill="#06b6d4" font-size="10">高折射率纤芯 n₁={{ n1.toFixed(2) }}</text>

          <!-- Laser Source Box -->
          <rect x="10" y="100" width="40" height="20" rx="4" fill="#3b82f6" />
          <circle x="50" y="110" r="3" fill="#fcd34d" />
          
          <!-- True Physically Traced Ray -->
          <path :d="rayPathData" fill="none" :stroke="leaking ? '#ef4444' : '#fcd34d'" stroke-width="3" :class="{'glow': !leaking}" />
          
          <!-- Leaked Rays visual indication -->
          <g v-if="leaking">
             <text x="250" y="30" fill="#ef4444" font-size="12" font-weight="bold">发生漏光 (不满足全反射)</text>
             <path v-for="(ray, i) in leakedRays" :key="i" :d="ray.d" fill="none" stroke="#ef4444" stroke-width="1.5" stroke-dasharray="4,2" />
          </g>
          <g v-else>
             <text x="250" y="30" fill="#22c55e" font-size="12" font-weight="bold">全反射约束 (无损传输)</text>
          </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>界面入射角 (与法线夹角)</label>
              <span class="val">{{ currentGrazingAngle.toFixed(1) }} <small>°</small></span>
           </div>
           <div class="hud-item orange">
              <label>全反射临界角</label>
              <span class="val">{{ criticalAngle.toFixed(1) }} <small>°</small></span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row flex flex-row items-center gap-4">
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">调节光源入射仰角 α</label>
                    <el-slider v-model="inputAngleDeg" :min="0" :max="45" :step="0.5" size="small"></el-slider>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>不再是生硬的 Z 字形，这是由真实的斯涅尔定律计算得出的光路模型！光从空气进入高折射率<strong>光纤芯 (n₁)</strong>，发生第一次折射。当光线打到光纤芯与<strong>包层 (n₂)</strong> 的交界面时，它与法线形成入射角θ。若此角大于由 <code>arcsin(n₂/n₁)</code> 计算出的全反射临界角，光将被 100% 锁死在纤芯中传递；反之，若光源初始仰角剥离过大，交界面入射角过小，部分能量就会突破包层折射泄漏（红色虚线）。这就是光纤和内窥镜无损传导的物理基石。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FiberOpticsViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      inputAngleDeg: 10,
      n0: 1.00,  // Air
      n1: 1.50,  // Core
      n2: 1.40   // Cladding
    }
  },
  computed: {
    criticalAngle() {
       return Math.asin(this.n2 / this.n1) * 180 / Math.PI;
    },
    // The angle at which the ray hits the core-cladding interface.
    // Core is horizontal. 
    // Snells law at entry: n0 * sin(inputAngle) = n1 * sin(theta_refract).
    thetaRefractRad() {
       let inputRad = this.inputAngleDeg * Math.PI / 180;
       return Math.asin(this.n0 * Math.sin(inputRad) / this.n1);
    },
    currentGrazingAngle() {
       // Angle with normal of the core-cladding boundary (which is vertical)
       // The ray is angled at thetaRefractRad from horizontal.
       // So the angle with the vertical normal is 90 - thetaRefractRad.
       return 90 - (this.thetaRefractRad * 180 / Math.PI);
    },
    leaking() {
       return this.currentGrazingAngle < this.criticalAngle;
    },
    rayData() {
       // Raytracing geometry setup
       let points = [];
       // Start outside
       let x = 50, y = 110;
       points.push({x, y});
       
       // Entry into fiber
       let entryX = 100;
       let dy = Math.tan(this.inputAngleDeg * Math.PI / 180) * 50;
       y -= dy; // Moving right & up (or down, since inputAngle>0, y decreases)
       x = entryX;
       points.push({x, y});
       
       // Inside fiber
       let coreWidth = 80;
       let coreTop = 70;
       let coreBottom = 150;
       
       // dx per y bounce
       let thetaRad = this.thetaRefractRad; // Angle relative to horizontal
       let dx = 0;
       if (thetaRad > 0.0001) dx = coreWidth / Math.tan(thetaRad);
       
       let leakedRays = [];
       
       if (thetaRad > 0.0001) {
          // Bounce logic
          // Determine first bounce (it's going UP towards coreTop)
          let distToTop = y - coreTop;
          let firstDx = distToTop / Math.tan(thetaRad);
          x += firstDx;
          y = coreTop;
          points.push({x, y});
          
          if (this.leaking) {
             // leaks out
             let refractAngle = Math.asin(this.n1 * Math.sin((Math.PI/2 - thetaRad)) / this.n2);
             // It exits into cladding
             let leakDx = 50 * Math.tan(refractAngle);
             leakedRays.push({ d: `M ${x},${y} L ${x + leakDx},${y - 50}` });
             
             // The main ray transmits a little and reflects a little, for simplicity we stop it or continue deeply dim.
             // We'll show one more bounce heavily attenuated
             x += dx; y = coreBottom;
             points.push({x, y});
             leakedRays.push({ d: `M ${x},${y} L ${x + leakDx},${y + 50}` });
          } else {
             // TIR
             while (x < 500) {
                x += dx; y = coreBottom;
                if (x > 500) { y -= (x - 500)*Math.tan(thetaRad); x = 500; points.push({x, y}); break; }
                points.push({x, y});
                
                x += dx; y = coreTop;
                if (x > 500) { y += (x - 500)*Math.tan(thetaRad); x = 500; points.push({x, y}); break; }
                points.push({x, y});
             }
          }
       } else {
          // Straight line
          points.push({x: 500, y: 110});
       }
       
       return {
          mainLine: "M " + points.map(p => `${p.x},${p.y}`).join(" L "),
          leakedRays
       };
    },
    rayPathData() { return this.rayData.mainLine; },
    leakedRays() { return this.rayData.leakedRays; }
  }
}
</script>

<style scoped lang="scss">
.fiber-optics-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } code { background: #e2e8f0; padding: 2px 4px; border-radius: 4px; font-family: monospace; } } }
.glow { filter: drop-shadow(0 0 6px #fcd34d); }
</style>
